import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Modal,
  Upload,
  Select,
  Button,
  Spin,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import SunEditor from 'suneditor-react';
import keyBy from 'lodash.keyby';
import { partnersQuery, upsertPartnerMutation } from '../../graphql/partner';
import validateImageFile from '../../utils/validateImageFile';
import LanguageTabs from '../LanguageTabs';
import { defaultButtonList } from '../../constants';

const ModalUpsertPartner = ({ selectedItem, modalOpen, closeModal }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);

  const [upsertPartner, { loading: upsertPartnerLoading }] = useMutation(
    upsertPartnerMutation
  );

  useEffect(() => {
    if (selectedItem) {
      form.setFieldsValue({
        ...selectedItem,
        // convert translations array to keyed object
        translations: keyBy(selectedItem.translations, 'languageIdentifier'),
      });
    }
  }, [selectedItem, form]);

  return (
    <Modal
      title={`${selectedItem ? 'Edit' : 'Create'} Partner`}
      afterClose={() => {
        form.resetFields();
        setFileList([]);
      }}
      onCancel={closeModal}
      maskClosable={false}
      forceRender={true}
      visible={modalOpen}
      footer={[
        <Button
          key="cancel"
          onClick={closeModal}
          disabled={upsertPartnerLoading}
        >
          Cancel
        </Button>,

        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          loading={upsertPartnerLoading}
          onClick={() => {
            form.submit();
          }}
        >
          {selectedItem ? 'Save' : 'Create'}
        </Button>,
      ]}
    >
      <Spin spinning={upsertPartnerLoading}>
        <Form
          layout="vertical"
          form={form}
          onFinishFailed={({ errorFields }) => setValidationErrors(errorFields)}
          onFinish={async (data) => {
            await upsertPartner({
              variables: {
                input: {
                  ...(selectedItem?.id && { id: selectedItem.id }),
                  ...data,
                  // convert transations back to array
                  translations: Object.entries(data.translations).map(
                    ([languageIdentifier, val]) => ({
                      languageIdentifier,
                      ...val,
                    })
                  ),
                },
              },
              refetchQueries: [
                {
                  query: partnersQuery,
                },
              ],
            });

            closeModal();
          }}
        >
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Partner Name"
                rules={[
                  { required: true, message: 'Partner name is required' },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="type"
                label="Partner Type"
                rules={[
                  { required: true, message: 'Partner Type is required' },
                ]}
              >
                <Select placeholder="Select Type...">
                  <Select.Option value="EXPORT">Export</Select.Option>
                  <Select.Option value="PRODUCTION">Production</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            <Col span={24}>
              <LanguageTabs validationErrors={validationErrors}>
                {(identifier) => (
                  <div>
                    <Form.Item
                      name={['translations', identifier, 'description']}
                      label="Description"
                      rules={[
                        { required: true, message: 'Description is required' },
                      ]}
                    >
                      <SunEditor
                        setContents={form.getFieldValue([
                          'translations',
                          identifier,
                          'description',
                        ])}
                        setOptions={{
                          height: 200,
                          buttonList: defaultButtonList,
                        }}
                      />
                    </Form.Item>
                  </div>
                )}
              </LanguageTabs>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item name="url" label="Partner URL">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name="image"
                label="Partner Logo"
                valuePropName="file"
                getValueFromEvent={(e) => e.file}
                rules={[
                  {
                    required: !!!selectedItem,
                    message: 'Partner Logo is required',
                  },
                ]}
              >
                <Upload
                  listType="picture"
                  multiple={false}
                  beforeUpload={validateImageFile}
                  fileList={fileList}
                  showUploadList={{
                    showRemoveIcon: false,
                  }}
                  onChange={({ fileList }) => {
                    setFileList(fileList.slice(-1));
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ModalUpsertPartner;
